import { useState, useEffect } from 'react';
import { setAuth, getTokenPayload, authFetch } from '../services/auth';
import { MARKETPLACE_URL } from '../config';

/**
 * MarketplaceRegister - Registration form for new AWS Marketplace customers.
 * 
 * This component is shown after the Marketplace redirect to collect
 * additional customer information (email, company name, country).
 * 
 * URL params expected:
 * - token: JWT token from marketplace_registration Lambda
 * - customer_id: AWS Marketplace customer identifier
 * - new_registration: "true" indicates first-time registration
 */
export function MarketplaceRegister({ onComplete }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [country, setCountry] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    initializeFromUrl();
  }, []);

  /**
   * Extract token and customer info from URL parameters.
   */
  function initializeFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const customerId = params.get('customer_id');
    const isNewRegistration = params.get('new_registration') === 'true';

    if (!token) {
      setError('Registration token not found. Please restart the process from AWS Marketplace.');
      setLoading(false);
      return;
    }

    // Decode token to get customer info
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid token format');
      }
      
      const payload = parts[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
      const decoded = JSON.parse(atob(padded));
      
      // Check if token is expired
      const now = Math.floor(Date.now() / 1000);
      if (decoded.exp && decoded.exp < now) {
        setError('The registration link has expired. Please restart the process from AWS Marketplace.');
        setLoading(false);
        return;
      }

      setTokenData({
        token,
        customerId: customerId || decoded.sub,
        productCode: decoded.product_code,
        isNewRegistration,
      });

      // Pre-fill email if available in token
      if (decoded.email) {
        setEmail(decoded.email);
      }
    } catch (e) {
      console.error('Failed to parse token:', e);
      setError('Invalid registration token. Please restart the process from AWS Marketplace.');
    }

    setLoading(false);
  }

  /**
   * Submit registration form to complete customer setup.
   */
  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!acceptTerms) {
      setError('You must accept the terms and conditions to continue.');
      return;
    }

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Store token temporarily to make authenticated request
      const user = {
        customerId: tokenData.customerId,
        email: email,
      };
      setAuth(tokenData.token, user);

      // Call API to complete registration
      const response = await authFetch('/marketplace/complete-registration', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          company_name: companyName,
          country,
          accepted_terms: acceptTerms,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Error completing registration');
      }

      const data = await response.json();
      
      // Update stored user with new token if provided
      if (data.token) {
        setAuth(data.token, {
          customerId: tokenData.customerId,
          email: email,
          companyName,
          country,
        });
      }

      // Clean URL and notify parent
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.toString());

      if (onComplete) {
        onComplete();
      } else {
        // Redirect to main app
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Registration failed:', err);
      setError(err.message || 'Error completing registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Skip detailed registration (just store basic token).
   * User can complete profile later.
   */
  function handleSkip() {
    if (!tokenData) return;

    // Store minimal auth data
    setAuth(tokenData.token, {
      customerId: tokenData.customerId,
      email: email || '',
    });

    // Clean URL
    const url = new URL(window.location.href);
    url.search = '';
    window.history.replaceState({}, '', url.toString());

    if (onComplete) {
      onComplete();
    } else {
      window.location.href = '/';
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-loading">
            <span className="spinner"></span>
            <p>Verifying registration...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state (no token or invalid)
  if (error && !tokenData) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Registration Error</h2>
          <div className="auth-error" style={{ marginTop: '1rem' }}>{error}</div>
          <a
            href={MARKETPLACE_URL}
            className="btn-primary"
            style={{ marginTop: '1.5rem', textDecoration: 'none', display: 'block', textAlign: 'center' }}
          >
            Go to AWS Marketplace
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: '480px' }}>
        <h2>Complete Your Registration</h2>
        <p className="auth-description">
          Your AWS Marketplace subscription is active. Complete your details to start using the service.
        </p>

        {tokenData?.customerId && (
          <div className="register-info">
            <span className="register-info-label">Customer ID:</span>
            <span className="register-info-value">{tokenData.customerId}</span>
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="companyName">Company Name</label>
            <input
              type="text"
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="My Company Inc."
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="country">Country</label>
            <select
              id="country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={submitting}
              className="form-select"
            >
              <option value="">Select country...</option>
              <option value="AR">Argentina</option>
              <option value="BR">Brazil</option>
              <option value="CL">Chile</option>
              <option value="CO">Colombia</option>
              <option value="MX">Mexico</option>
              <option value="PE">Peru</option>
              <option value="UY">Uruguay</option>
              <option value="US">United States</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div className="form-group form-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                disabled={submitting}
              />
              <span>
                I accept the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  terms and conditions
                </a>
                {' '}and the{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  privacy policy
                </a>
              </span>
            </label>
          </div>

          {error && (
            <div className="auth-error">{error}</div>
          )}

          <button
            type="submit"
            className="btn-primary"
            disabled={submitting || !acceptTerms}
          >
            {submitting ? (
              <>
                <span className="spinner"></span>
                Completing registration...
              </>
            ) : (
              'Complete Registration'
            )}
          </button>

          <button
            type="button"
            className="btn-secondary"
            onClick={handleSkip}
            disabled={submitting}
          >
            Complete Later
          </button>
        </form>
      </div>
    </div>
  );
}
